/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { generatePatternStyles } from '@web-stories-wp/patterns';
import { rgba } from 'polished';

/**
 * Internal dependencies
 */
import { isEqual } from '../../../../utils/isEqual';

const LEGACY_OVERLAY_ID = 'legacy-overlay';
const ROTATED_RADIAL_OVERLAY_SCALE = 3;
const VECTOR_EPSILON = 0.0001;

export function normalizeOverlayFilter(filter, index = 0) {
  if (!filter) {
    return null;
  }

  const pattern = Object.prototype.hasOwnProperty.call(filter, 'pattern')
    ? filter.pattern
    : filter;
  if (!pattern) {
    return null;
  }

  return {
    id: filter.id || `${LEGACY_OVERLAY_ID}-${index}`,
    pattern,
    isHidden: Boolean(filter.isHidden),
    blur: filter.blur || 0,
  };
}

export function getOverlayStack(element) {
  if (!element) {
    return [];
  }

  const legacyStack = normalizeOverlayFilter(
    {
      id: LEGACY_OVERLAY_ID,
      pattern: element.overlay,
    },
    0
  );

  if (Array.isArray(element.overlays)) {
    const stack = element.overlays
      .map((filter, index) => normalizeOverlayFilter(filter, index))
      .filter(Boolean);

    if (!Object.prototype.hasOwnProperty.call(element, 'overlay')) {
      return stack;
    }

    const legacyOverlay = legacyStack?.pattern || null;
    const stackLegacyOverlay = getLegacyOverlayFromStack(stack);
    if (legacyOverlay && !isEqual(stackLegacyOverlay, legacyOverlay)) {
      return [legacyStack];
    }

    if (element.overlay === null && stackLegacyOverlay) {
      return [];
    }

    return stack;
  }

  return legacyStack ? [legacyStack] : [];
}

export function getVisibleOverlayStack(element) {
  return getOverlayStack(element).filter(({ isHidden }) => !isHidden);
}

export function getOverlayRenderStack(element) {
  return getVisibleOverlayStack(element).slice().reverse();
}

export function getLegacyOverlayFromStack(overlays = []) {
  const filter = overlays.find(({ isHidden, pattern }) => !isHidden && pattern);
  return filter?.pattern || null;
}

export function getOverlayStyleProperties(element) {
  if (
    !element ||
    (!Object.prototype.hasOwnProperty.call(element, 'overlay') &&
      !Object.prototype.hasOwnProperty.call(element, 'overlays'))
  ) {
    return {};
  }

  const overlays = getOverlayStack(element);
  return {
    overlay: getLegacyOverlayFromStack(overlays),
    overlays,
  };
}

function getScaledRadialValue(value = 0.5) {
  return (
    (value + (ROTATED_RADIAL_OVERLAY_SCALE - 1) / 2) /
    ROTATED_RADIAL_OVERLAY_SCALE
  );
}

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function truncate(value, precision = 2) {
  return Number(value.toFixed(precision));
}

function normalizeTurn(value) {
  return ((value % 1) + 1) % 1;
}

function getLinearStopList(stops = [], alpha = 1, getPosition) {
  return stops.map(({ color, position }) => {
    const { r, g, b, a = 1 } = color;
    return `${rgba(r, g, b, a * alpha)} ${truncate(
      getPosition(clamp(position)) * 100
    )}%`;
  });
}

function getLinearEndpointOverlayStyle(pattern, box = {}) {
  const { end, start, alpha = 1, stops } = pattern;

  if (!start || !end || !Array.isArray(stops)) {
    return null;
  }

  const width = box.width || 1;
  const height = box.height || 1;
  const startPoint = {
    x: clamp(start.x) * width,
    y: clamp(start.y) * height,
  };
  const endPoint = {
    x: clamp(end.x) * width,
    y: clamp(end.y) * height,
  };
  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length < VECTOR_EPSILON) {
    return null;
  }

  const axis = {
    x: dx / length,
    y: dy / length,
  };
  const fullLength = Math.abs(axis.x) * width + Math.abs(axis.y) * height;

  if (fullLength < VECTOR_EPSILON) {
    return null;
  }

  const centerProjection = (width * axis.x + height * axis.y) / 2;
  const minProjection = centerProjection - fullLength / 2;
  const getProjectedPosition = ({ x, y }) =>
    (x * axis.x + y * axis.y - minProjection) / fullLength;
  const startPosition = getProjectedPosition(startPoint);
  const endPosition = getProjectedPosition(endPoint);
  const angleTurn = normalizeTurn(Math.atan2(dy, dx) / (Math.PI * 2) + 0.25);
  const stopList = getLinearStopList(
    stops,
    alpha,
    (position) => startPosition + (endPosition - startPosition) * position
  );

  return {
    backgroundImage: `linear-gradient(${angleTurn}turn, ${stopList.join(
      ', '
    )})`,
  };
}

function getRotatedRadialOverlayStyle(pattern) {
  const center = pattern.center || { x: 0.5, y: 0.5 };
  const size = pattern.size || { w: 1, h: 1 };
  const scaledCenter = {
    x: getScaledRadialValue(center.x),
    y: getScaledRadialValue(center.y),
  };

  return {
    ...generatePatternStyles({
      ...pattern,
      center: scaledCenter,
      size: {
        w: size.w / ROTATED_RADIAL_OVERLAY_SCALE,
        h: size.h / ROTATED_RADIAL_OVERLAY_SCALE,
      },
    }),
    height: `${ROTATED_RADIAL_OVERLAY_SCALE * 100}%`,
    left: `${((1 - ROTATED_RADIAL_OVERLAY_SCALE) / 2) * 100}%`,
    top: `${((1 - ROTATED_RADIAL_OVERLAY_SCALE) / 2) * 100}%`,
    transform: `rotate(${pattern.rotation}turn)`,
    transformOrigin: `${scaledCenter.x * 100}% ${scaledCenter.y * 100}%`,
    width: `${ROTATED_RADIAL_OVERLAY_SCALE * 100}%`,
  };
}

export function getOverlayFilterStyle(filter, box) {
  if (filter.pattern?.geometryVersion === 2) {
    const style = generatePatternStyles(
      filter.pattern,
      'background',
      null,
      box
    );
    return filter.blur ? { ...style, filter: `blur(${filter.blur}px)` } : style;
  }
  const shouldRotateRadial =
    filter.pattern?.type === 'radial' && filter.pattern.rotation;
  const linearEndpointStyle =
    filter.pattern?.type === 'linear'
      ? getLinearEndpointOverlayStyle(filter.pattern, box)
      : null;
  const style =
    linearEndpointStyle ||
    (shouldRotateRadial
      ? getRotatedRadialOverlayStyle(filter.pattern)
      : generatePatternStyles(filter.pattern));

  if (!filter.blur) {
    return style;
  }

  return {
    ...style,
    filter: `blur(${filter.blur}px)`,
  };
}
